import React, { useState, useEffect, useRef } from "react"
import { Formik, Form } from "formik"
import TextField from "@material-ui/core/TextField"
import { Grid } from "@material-ui/core"
import DialogContent from "@material-ui/core/DialogContent"
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete"
import api from "../../services/api"
import toastError from "../../errors/toastError"
import { toast } from "react-toastify"
import useMotivoStore from "../../stores/motivoStore"
import DefaultButton from "../DefaultButton"
import useStyles from "./TransferTicketModalCustomStyles"
import Modal from "../Modal"
import { ChevronDown, X } from "lucide-react"
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min"
import { Skeleton } from "@material-ui/lab"
import { useFilaStore } from "../../stores/filaStore"

const filterOptions = createFilterOptions({
  trim: true,
})

const TransferTicketModalCustom = ({ modalOpen, onClose, ticketid, moveCard, aoConcluirTransferencia, nSituacaoOrigemId }) => {
  const { filas: queues, fetchFilas } = useFilaStore()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [prevQueue, setPrevQueue] = useState()
  const [selectedQueue, setSelectedQueue] = useState(null) // Armazena o ID da fila
  const [selectedMotivo, setSelectedMotivo] = useState(null) // Armazena o ID do motivo
  const [selectedUser, setSelectedUser] = useState(null)
  const [initialValues, setInitialValues] = useState({
    selectedQueue: null, // Armazena o ID da fila
    selectedUser: null, // Armazena o ID do usuário
    motivoContatoId: null, // Armazena o ID do motivo
    nSituacaoId: null,
  })
  const history = useHistory()
  const classes = useStyles()
  const isMounted = useRef(true)
  const { motivos, fetchMotivos } = useMotivoStore()
  const formikRef = useRef(null)
  const location = useLocation()

  useEffect(async () => {
    if (!modalOpen) return
    await fetchMotivos()
    await fetchFilas()
  }, [fetchMotivos, modalOpen, fetchFilas])

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (modalOpen) {
      const fetchTicketDetails = async () => {
        try {
          const { data } = await api.get(`/tickets/${ticketid}`)

          // Define os IDs iniciais
          setSelectedUser(data.responsavelId)
          setSelectedQueue(data.queueId)
          setPrevQueue(data.queueId)
          setSelectedMotivo(data.motivoContatoId)

          // Define os valores iniciais do Formik
          setInitialValues({
            selectedQueue: data.queueId || null,
            selectedUser: data.responsavelId || null,
            motivoContatoId: data.motivoContatoId || null,
            nSituacaoId: data.situacaoId || null,
          })
        } catch (err) {
          toastError(err)
        }
      }
      fetchTicketDetails()
    }
  }, [modalOpen, ticketid])

  useEffect(() => {
    let isMounted = true
    setLoading(true)

    const fetchUsers = async () => {
      try {
        if (selectedQueue) {
          const { data } = await api.get("/users", {
            params: { filas: JSON.stringify([selectedQueue]), ativo: true },
          })
          if (isMounted) {
            setUsers(data.users)
          }
        } else if (isMounted) {
          setUsers([])
        }
        if (isMounted) {
          setLoading(false)
        }
      } catch (err) {
        if (isMounted) {
          setLoading(false)
          toastError(err)
        }
      }
    }

    const delayDebounceFn = setTimeout(() => {
      fetchUsers()
    }, 500)

    return () => {
      isMounted = false
      clearTimeout(delayDebounceFn)
    }
  }, [selectedQueue, modalOpen])

  const handleClose = () => {
    onClose()
    handleClean()
  }

  const handleClean = () => {
    setUsers([])
    setSelectedUser(null)
    setSelectedQueue(null)
    setSelectedMotivo(null)
  }

  const handleSaveTicket = async (values) => {
    if (!ticketid) return
    if (!values.selectedQueue || values.selectedQueue === "") return

    setLoading(true)
    try {
      let data = {}

      if (values.selectedUser) {
        data.responsavelId = Number(values.selectedUser)
        data.status = "open"
      }

      if (values.selectedQueue && values.selectedQueue !== null) {
        data.queueId = values.selectedQueue

        if (!values.selectedUser) {
          data.status = "pending"
          data.responsavelId = null
        }
      }

      data.motivoContatoId = values.motivoContatoId

      const oDadosAtualizados = await api.put(`/tickets/${ticketid}`, data)

      if (location.pathname.includes("fluxo")) {
        moveCard(initialValues.motivoContatoId, oDadosAtualizados.data.motivoContatoId)
      } else if (location.pathname.includes("kanban")) {
        aoConcluirTransferencia?.(oDadosAtualizados.data, nSituacaoOrigemId ?? initialValues.nSituacaoId)
      }

      location.pathname.includes("conversas") && prevQueue != values.selectedQueue && history.push(`/conversas`)
      toast.success("Conversa transferida com sucesso!")
      handleClose()
    } catch (err) {
      setLoading(false)
      toastError(err)
    }
  }

  const submitTransferTicket = async () => {
    await formikRef.current.submitForm()
  }

  const Botoes = () => {
    return (
      <div className={classes.backgroundBotoes}>
        <div className={classes.buttonsContainer}>
          <DefaultButton
            stylization="primary"
            type="submit"
            disabled={
              !selectedQueue ||
              !selectedMotivo ||
              loading ||
              (selectedUser === initialValues.selectedUser &&
                selectedQueue === initialValues.selectedQueue &&
                selectedMotivo === initialValues.motivoContatoId)
            }
            loading={loading}
            onClick={submitTransferTicket}
            className={classes.button}
          >
            Transferir
          </DefaultButton>
        </div>
      </div>
    )
  }

  return (
    <Modal
      title={"Transferir conversa"}
      modalOpen={modalOpen}
      onClose={onClose}
      width="400px"
      footer={<Botoes values={formikRef.current?.values || {}} />}
    >
      <DialogContent className={classes.content}>
        <Formik innerRef={formikRef} enableReinitialize initialValues={initialValues} onSubmit={handleSaveTicket}>
          {({ setFieldValue, values }) => (
            <Form>
              {loading ? (
                <Skeleton animation="wave" height={80} width={330} />
              ) : (
                <Grid xs={12} style={{ marginTop: "8px" }} item>
                  <Autocomplete
                    className={classes.autocomplete}
                    fullWidth
                    value={queues.find((q) => q.id === values.selectedQueue) || null}
                    onChange={(event, newValue) => {
                      const queueId = newValue ? newValue.id : null
                      setFieldValue("selectedQueue", queueId)
                      setSelectedQueue(queueId)

                      // Limpa a lista de usuários ao mudar a fila
                      setFieldValue("selectedUser", null)
                      setUsers([])
                      setSelectedUser(null)
                    }}
                    popupIcon={<ChevronDown className={classes.iconChevronDown} strokeWidth={2.0} size={"24px"} />}
                    closeIcon={<X className={classes.iconX} strokeWidth={2.0} size={"24px"} />}
                    options={queues}
                    getOptionLabel={(option) => `${option.name}`}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Transferir para fila"
                        fullWidth
                        autoFocus
                        InputLabelProps={{
                          classes: {
                            root: classes.label,
                          },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          classes: { input: classes.textField },
                        }}
                      />
                    )}
                  />
                </Grid>
              )}

              {loading ? (
                <Skeleton animation="wave" height={80} width={330} />
              ) : (
                <Grid xs={12} style={{ marginTop: "24px" }} item>
                  <Autocomplete
                    className={classes.autocomplete}
                    fullWidth
                    value={users.find((u) => u.id === values.selectedUser) || null}
                    onChange={(event, newValue) => {
                      const userId = newValue ? newValue.id : null
                      setFieldValue("selectedUser", userId)
                      setSelectedUser(userId)
                    }}
                    popupIcon={<ChevronDown className={classes.iconChevronDown} strokeWidth={2.0} size={"24px"} />}
                    closeIcon={<X className={classes.iconX} strokeWidth={2.0} size={"24px"} />}
                    options={users}
                    getOptionLabel={(option) => `${option.name}`}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    filterOptions={filterOptions}
                    noOptionsText="Nenhum usuário encontrado"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Selecione um usuário"
                        fullWidth
                        autoFocus
                        InputLabelProps={{
                          classes: {
                            root: classes.label,
                          },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          classes: { input: classes.textField },
                        }}
                      />
                    )}
                  />
                </Grid>
              )}

              {loading ? (
                <Skeleton animation="wave" height={80} width={330} />
              ) : (
                <Grid container style={{ marginTop: "24px" }}>
                  <Grid xs={12} item>
                    <Autocomplete
                      className={classes.autocomplete}
                      fullWidth
                      value={motivos.find((m) => m.id === values.motivoContatoId) || null}
                      onChange={(event, newValue) => {
                        const motivoId = newValue ? newValue.id : null
                        setFieldValue("motivoContatoId", motivoId)
                        setSelectedMotivo(motivoId)
                      }}
                      popupIcon={<ChevronDown className={classes.iconChevronDown} strokeWidth={2.0} size={"24px"} />}
                      closeIcon={<X className={classes.iconX} strokeWidth={2.0} size={"24px"} />}
                      options={motivos}
                      getOptionLabel={(option) => option.descricao}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Selecione um Motivo do Contato"
                          fullWidth
                          InputLabelProps={{
                            classes: {
                              root: classes.label,
                            },
                          }}
                          InputProps={{
                            ...params.InputProps,
                            classes: { input: classes.textField },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              )}
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Modal>
  )
}

export default TransferTicketModalCustom
